<template>
  <layout-basic>
    <page-hero
      title="Real Businesses. Real Results."
      lead="See how organisations are transforming onboarding, compliance and employee development."
    />

    <section class="demo-video-section">
      <v-container style="max-width: 960px">
        <demo-video video-key="stories" />
      </v-container>
    </section>

    <!-- Trilogy Active, the first named customer story (Steve, 16 Sep 2026):
         the facts he supplied and the quote, verbatim. -->
    <section class="stories-featured">
      <v-container>
        <article class="stories-featured__card">
          <p class="stories-featured__eyebrow">Customer story</p>
          <h2 class="font-sora stories-featured__name">{{ story.company }}</h2>
          <p class="stories-featured__meta">
            <span>{{ story.sector }}</span>
            <span class="stories-featured__sep" aria-hidden="true">·</span>
            <span>{{ story.facts }}</span>
          </p>
          <blockquote class="stories-featured__quote">“{{ story.quote }}”</blockquote>
          <p class="stories-featured__attrib">
            <strong>{{ story.attribution }}</strong><br />{{ story.role }}
          </p>
        </article>
      </v-container>
    </section>

    <section class="stories-sectors">
      <v-container>
        <h2 class="font-sora stories-sectors__title">Customer stories by sector</h2>
        <p class="stories-sectors__note">
          What customers in each sector tell us. Full case studies are on the way.
        </p>
        <div class="stories-sectors__grid">
          <div
            v-for="sector in sectors"
            :key="sector.name"
            class="stories-sectors__card"
            :class="{ 'stories-sectors__card--quoted': sector.quotes && sector.quotes.length }"
          >
            <strong>{{ sector.name }}</strong>
            <template v-if="sector.quotes && sector.quotes.length">
              <blockquote v-for="(q, i) in sector.quotes" :key="i" class="stories-sectors__quote">
                “{{ q.quote }}”
                <cite>{{ q.attribution }}</cite>
              </blockquote>
            </template>
            <span v-else>Case study coming soon</span>
          </div>
        </div>
      </v-container>
    </section>

    <standard-cta
      title="Could your business be next?"
      lead="Watch the 5-minute demo or experience IMPROVE for yourself."
    />
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import PageHero from '@/components/marketing/PageHero.vue';
import StandardCta from '@/components/marketing/StandardCta.vue';
import DemoVideo from '@/components/DemoVideo.vue';
import { marketingPageMeta } from '@/utils/marketingPage';
import {
  TRILOGY_STORY,
  TRILOGY_RENEWED,
  CHARITY_VALUE_FOR_MONEY,
  CHARITY_LEARNING_PATHWAYS,
} from '@/data/testimonials';

export default {
  name: 'CustomerStories',
  components: {
    LayoutBasic,
    PageHero,
    StandardCta,
    DemoVideo,
  },
  metaInfo() {
    return marketingPageMeta({
      title: 'Real Businesses. Real Results. | IMPROVE',
      description:
        'See how organisations are transforming onboarding, compliance and employee development with IMPROVE.',
      path: '/customer-stories',
    });
  },
  data() {
    return {
      story: TRILOGY_STORY,
      // The two charity quotes from Steve's 16 Sep brief were headed "Charity
      // Sector Page"; there are no sector pages and he wants no new ones, so
      // they live in the sector card here. Trilogy's renewal quote likewise.
      sectors: [
        { name: 'Manufacturing' },
        { name: 'Engineering' },
        { name: 'Professional Services' },
        { name: 'Charity', quotes: [CHARITY_VALUE_FOR_MONEY, CHARITY_LEARNING_PATHWAYS] },
        { name: 'Not-for-Profit / Health, Wellbeing & Leisure', quotes: [TRILOGY_RENEWED] },
        { name: 'Logistics' },
      ],
    };
  },
};
</script>

<style scoped>
.stories-sectors {
  padding: 32px 0 48px;
  background: #f7f9fb;
}

.stories-sectors__title {
  text-align: center;
  color: var(--brand-navy, #002458);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 12px;
}

.stories-sectors__note {
  text-align: center;
  max-width: 560px;
  margin: 0 auto 28px;
  color: var(--brand-grey, #6b7280);
  font-size: 15px;
  line-height: 1.5;
}

.stories-sectors__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  max-width: 980px;
  margin: 0 auto;
}

.stories-sectors__card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 22px 18px;
  color: var(--brand-navy, #002458);
}

.stories-sectors__card strong {
  font-size: 17px;
}

.stories-sectors__card--quoted {
  grid-column: span 1;
}

.stories-sectors__quote {
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--brand-navy, #002458);
}

.stories-sectors__quote cite {
  display: block;
  margin-top: 4px;
  font-style: normal;
  font-size: 13px;
  color: var(--brand-grey, #6b7280);
}

.stories-featured {
  padding: 8px 0 24px;
}

.stories-featured__card {
  max-width: 900px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 14px;
  padding: 32px 36px;
  box-shadow: 0 12px 40px rgba(0, 36, 88, 0.06);
  text-align: center;
}

.stories-featured__eyebrow {
  margin: 0 0 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--brand-teal, #08a0a8);
}

.stories-featured__name {
  margin: 0 0 6px;
  font-size: 28px;
  font-weight: 700;
  color: var(--brand-navy, #002458);
}

.stories-featured__meta {
  margin: 0 0 18px;
  color: var(--brand-grey, #6b7280);
  font-size: 14px;
}

.stories-featured__sep {
  margin: 0 8px;
}

.stories-featured__quote {
  margin: 0 auto 14px;
  max-width: 760px;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.45;
  color: var(--brand-navy, #002458);
}

.stories-featured__attrib {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--brand-grey, #6b7280);
}

.stories-featured__attrib strong {
  color: var(--brand-navy, #002458);
}

.stories-sectors__card span {
  color: var(--brand-grey, #6b7280);
  font-size: 14px;
}

@media screen and (max-width: 900px) {
  .stories-sectors__grid {
    grid-template-columns: 1fr;
  }
}
</style>
